/**
 * Multi-row form field for entering external image URLs (up to maxUrls, default
 * 12). Each row has a text input (typed or pasted), a live thumbnail preview,
 * and a remove button; URLs are validated with isValidImageUrl plus the image's
 * own load/error events, with invalid rows highlighted and surfaced through
 * ErrorContext toasts. Parent owns the list via onUpdateImageUrl/onAddImageUrl/
 * onRemoveImageUrl callbacks; supports a readonly display mode.
 * Used by SubmissionCreate and the admin submission-verification form.
 */
import React, { useState, useEffect } from "react";
import { Trash2, AlertCircle, Info } from "lucide-react";
import { useError } from "@/contexts/ErrorContext";
import { isValidImageUrl } from "@/utils/submission";

interface ImageUrlsFieldProps {
    imageUrls: string[];
    onUpdateImageUrl: (index: number, value: string) => void;
    onAddImageUrl: () => void;
    onRemoveImageUrl: (index: number) => void;
    readonly?: boolean;
    maxUrls?: number;
}

const ImageUrlsField: React.FC<ImageUrlsFieldProps> = ({
    imageUrls,
    onUpdateImageUrl,
    onAddImageUrl,
    onRemoveImageUrl,
    readonly = false,
    maxUrls = 12,
}) => {
    const [invalidUrls, setInvalidUrls] = useState<Set<number>>(new Set());
    const { showError } = useError();

    const handleImageError = (index: number) => {
        setInvalidUrls((prev) => new Set(prev).add(index));
    };

    const handleImageLoad = (index: number) => {
        setInvalidUrls((prev) => {
            const newSet = new Set(prev);
            newSet.delete(index);
            return newSet;
        });
    };

    const handleUrlChange = (index: number, value: string) => {
        onUpdateImageUrl(index, value);

        // Validate URL immediately
        if (value.trim() && !isValidImageUrl(value)) {
            setInvalidUrls((prev) => new Set(prev).add(index));
        } else {
            setInvalidUrls((prev) => {
                const newSet = new Set(prev);
                newSet.delete(index);
                return newSet;
            });
        }
    };

    const handlePaste = (index: number, e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData("text");

        onUpdateImageUrl(index, pastedText);

        // Validate pasted URL immediately
        if (pastedText.trim() && !isValidImageUrl(pastedText)) {
            setInvalidUrls((prev) => new Set(prev).add(index));
        } else {
            setInvalidUrls((prev) => {
                const newSet = new Set(prev);
                newSet.delete(index);
                return newSet;
            });
        }
    };

    // Show error toast when there are invalid URLs
    useEffect(() => {
        const invalidCount = imageUrls.filter(
            (url, index) => url.trim() && (invalidUrls.has(index) || !isValidImageUrl(url))
        ).length;

        if (invalidCount > 0) {
            const totalUrls = imageUrls.filter((url) => url.trim() !== "").length;
            showError(
                `${invalidCount} of ${totalUrls} image URL${totalUrls > 1 ? "s" : ""} ${
                    invalidCount > 1 ? "are" : "is"
                } invalid, blocked, or cannot be loaded. Only public HTTP/HTTPS image URLs are allowed.`
            );
        }
    }, [invalidUrls, imageUrls, showError]);

    return (
        <div className="relative">
            <div className="flex items-center mb-2">
                <label className="block text-sm font-medium tracking-widest">
                    image urls - paste links ({imageUrls.length}/{maxUrls})
                </label>
                <div className="relative ml-2 group">
                    <Info className="w-4 h-4 text-gray-500 cursor-help" />
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-80 bg-black text-white text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                        <div className="text-center">
                            <img
                                src="/copyImageAddress.gif"
                                alt="How to copy image address"
                                className="w-72 h-72 mx-auto mb-2 rounded"
                            />
                            <div className="font-medium mb-1">How to get an image URL:</div>
                            <div>
                                Right-click (long-press on mobile) and press "Copy Image Address."
                            </div>
                            <div className="mt-1">
                                If this doesn't work, drag the image to the URL bar and copy it.
                            </div>
                        </div>
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-black"></div>
                    </div>
                </div>
            </div>
            {imageUrls.map((url, index) => {
                const isInvalid = invalidUrls.has(index) || (url.trim() && !isValidImageUrl(url));
                const hasUrl = url.trim() !== "";

                return (
                    <div key={index} className="flex items-center space-x-2 mb-2">
                        <div className="flex-1 relative">
                            <input
                                type="text"
                                value={url}
                                onChange={(e) => handleUrlChange(index, e.target.value)}
                                onPaste={(e) => handlePaste(index, e)}
                                placeholder={`paste image url ${index + 1}`}
                                readOnly={readonly}
                                className={`w-full px-2 py-3 border rounded-lg focus:border-black focus:outline-none tracking-wider text-xs ${
                                    readonly ? "bg-gray-100 cursor-not-allowed" : ""
                                } ${isInvalid ? "border-red-500 bg-red-50" : ""}`}
                            />
                            {isInvalid && (
                                <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                                    <AlertCircle className="w-4 h-4 text-red-500" />
                                </div>
                            )}
                        </div>

                        {hasUrl && (
                            <div className="relative">
                                {isInvalid ? (
                                    <div className="w-12 h-12 bg-red-100 border-2 border-red-300 rounded flex items-center justify-center">
                                        <AlertCircle className="w-6 h-6 text-red-500" />
                                    </div>
                                ) : (
                                    <a
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block"
                                    >
                                        <img
                                            src={url}
                                            alt={`Image ${index + 1}`}
                                            className="w-12 h-12 object-cover rounded cursor-pointer hover:opacity-75"
                                            onError={() => handleImageError(index)}
                                            onLoad={() => handleImageLoad(index)}
                                        />
                                    </a>
                                )}
                            </div>
                        )}

                        {!readonly && imageUrls.length > 1 && (
                            <button
                                onClick={() => {
                                    // Update invalidUrls state to account for index shift
                                    setInvalidUrls((prev) => {
                                        const newSet = new Set<number>();
                                        prev.forEach((invalidIndex) => {
                                            if (invalidIndex < index) {
                                                // Keep indices before the removed item unchanged
                                                newSet.add(invalidIndex);
                                            } else if (invalidIndex > index) {
                                                // Shift indices after the removed item down by 1
                                                newSet.add(invalidIndex - 1);
                                            }
                                            // Skip the removed index itself
                                        });
                                        return newSet;
                                    });
                                    onRemoveImageUrl(index);
                                }}
                                className="p-1 text-red-600 hover:text-red-800"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                );
            })}
            {!readonly && imageUrls.length < maxUrls && (
                <button
                    onClick={onAddImageUrl}
                    className="mt-2 px-3 py-1 text-sm bg-gray-200 hover:bg-gray-300 rounded-lg"
                >
                    + add image url
                </button>
            )}
        </div>
    );
};

export default ImageUrlsField;
